'use client'

import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { toast } from 'react-toastify'

import { useRouter } from '@/_i18n/routing'
import axios from '@/_lib/axiosInstance'
import { ApiResponse } from '@/_types'
import { CartProduct, CartProductType } from '@/app/[locale]/order/_context/order-flow-context'
import {
	clearGa4PendingFamilyPurchase,
	commitGa4FamilyPurchaseSnapshot,
	saveOrderUlid,
	savePayFormHtml
} from '@/app/[locale]/order/_utils/payment-utils'
import { isAllAnnualProgramFamily } from '@cart/_utils/is-all-annual-program-family'

// ---------------------------------------------------------------------------
// API types
// ---------------------------------------------------------------------------

type OrderApiType =
	| 'timeslot'
	| 'preventiveExamination'
	| 'yearProgram'
	| 'priceListItem'
	| 'priceListPromo'
	| 'creditProgram'

interface OrderRequest {
	type: OrderApiType
	patient: {
		firstName: string
		lastName: string
		email: string | null
		phone: string | null
		birthNumber: string
		addressStreetAndStreetNumber: string
		addressCity: string
		addressZipCode: string
		insuranceCompanyId?: number
		countryId?: string
		euInsuranceNumber?: string
		contractualInsurance?: string
	}
	customer?: {
		firstName: string
		lastName: string
		email: string
		phone: string
	}
	orderProducts: Array<{
		productId: number
		modules: number[]
	}>
	agreedToTermsOfService: boolean
	agreedToPersonalDataProtection: boolean
	agreedToPersonalDataProcessingForMarketingPurposes: boolean
	note?: string
}

interface OrderResponse {
	id: string
	status: string
	payFormHtml?: string | null
	statusTextOnly?: string | null
	order: { ulid: string }
}

interface CreateOrderResponse extends ApiResponse<OrderResponse> {}

// ---------------------------------------------------------------------------
// Payload shape passed from the form
// ---------------------------------------------------------------------------

/**
 * Values collected in the final order form plus cart metadata for {@link createOrder}.
 *
 * @remarks `cartProducts` determines API `type`, `orderProducts`, and module lists via internal mappers.
 */
export interface SubmitOrderPayload {
	firstName: string
	lastName: string
	email: string
	phone: string
	birthNumber: string
	insurance?: string
	euInsuranceNumber?: string
	contractualInsurance?: string
	countryId?: string
	address: string
	city: string
	zipCode: string
	message: string
	privacyPolicy: boolean
	marketing: boolean
	terms: boolean
	parent: {
		firstName: string
		lastName: string
		email: string
		phone: string
	}
	/** Derived from birth number age < 18 */
	isChildOrder: boolean
	/** True when the selected insurance company is an EU insurer */
	isEUInsurance: boolean
	cartProducts: CartProduct[]
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getOrderApiType(product: CartProduct): OrderApiType {
	switch (product.type) {
		case CartProductType.TEMPLATE:
			return 'timeslot'
		case CartProductType.PRICE_LIST_ITEM:
			return 'priceListItem'
		case CartProductType.ANNUAL_PROGRAM:
		case CartProductType.ANNUAL_PROGRAM_FAMILY:
			return 'yearProgram'
		case CartProductType.PREVENTIVE_EXAM:
			return 'preventiveExamination'
		case CartProductType.CREDIT_PROGRAM:
			return 'creditProgram'
		case CartProductType.PROMOTION:
			return 'priceListPromo'
	}
}

function getProductId(product: CartProduct): number {
	switch (product.type) {
		case CartProductType.TEMPLATE:
			return product.data.timeslotId
		case CartProductType.PRICE_LIST_ITEM:
			return product.data.itemId
		default:
			return product.data.programId
	}
}

function getProductModules(product: CartProduct): number[] {
	switch (product.type) {
		case CartProductType.ANNUAL_PROGRAM:
		case CartProductType.ANNUAL_PROGRAM_FAMILY:
		case CartProductType.PREVENTIVE_EXAM:
			return product.data.extraModuleIds ?? []
		default:
			return []
	}
}

// ---------------------------------------------------------------------------
// Create-order API error handling (422 / domain errors)
// ---------------------------------------------------------------------------

interface CreateOrderErrorResponseBody {
	error?: string
	errorData?: unknown[]
	errorDataObject?: { modules?: unknown[] }
}

const CREATE_ORDER_KNOWN_ERROR_MESSAGES: Record<string, string> = {
	'sanitas.error.ecommerce.order.module.minimumPatientAgeNotMet':
		'Vek pacienta je menší ako minimálny vek stanovený pre tento modul. Upravte výber modulov alebo údaje pacienta.'
}

function extractCreateOrderErrorCodes(data: unknown): string[] {
	if (!data || typeof data !== 'object') {
		return []
	}
	const d = data as CreateOrderErrorResponseBody
	const codes = new Set<string>()
	if (typeof d.error === 'string' && d.error.length > 0) {
		codes.add(d.error)
	}
	if (Array.isArray(d.errorData)) {
		for (const item of d.errorData) {
			if (typeof item === 'string' && item.length > 0) {
				codes.add(item)
			}
		}
	}
	const modules = d.errorDataObject?.modules
	if (Array.isArray(modules)) {
		for (const m of modules) {
			if (typeof m === 'string' && m.length > 0) {
				codes.add(m)
			}
		}
	}
	return [...codes]
}

function getMappedCreateOrderErrorMessage(data: unknown): string | null {
	for (const code of extractCreateOrderErrorCodes(data)) {
		const message = CREATE_ORDER_KNOWN_ERROR_MESSAGES[code]
		if (message) {
			return message
		}
	}
	return null
}

const CREATE_ORDER_ERROR_TOAST_OPTIONS = {
	position: 'top-right' as const,
	className: '!bg-medante-error',
	autoClose: 5000,
	hideProgressBar: false,
	closeOnClick: true,
	pauseOnHover: true
}

/**
 * Submits a normalized `OrderRequest` to the public ecommerce orders endpoint.
 *
 * @param orderData - Fully built request body (patient, consents, `orderProducts`, etc.).
 * @returns Raw {@link CreateOrderResponse} including `order.ulid` and optional `payFormHtml`.
 */
export const createOrder = async (orderData: OrderRequest): Promise<CreateOrderResponse> => {
	const response = await axios.post<CreateOrderResponse>('v1/web/ecommerce/orders', orderData)
	return response.data
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Mutation hook that maps {@link SubmitOrderPayload} to {@link OrderRequest}, posts {@link createOrder},
 * then routes to payment or a fallback page and surfaces toast feedback.
 *
 * @returns TanStack `useMutation` result typed as `CreateOrderResponse` on success.
 */
export const useCreateOrder = () => {
	const router = useRouter()

	return useMutation<CreateOrderResponse, Error, SubmitOrderPayload>({
		mutationFn: async payload => {
			const { cartProducts, isChildOrder, isEUInsurance } = payload

			if (cartProducts.length === 0) {
				throw new Error('No products in cart')
			}

			const primaryProduct = cartProducts[0]

			const patient: OrderRequest['patient'] = {
				firstName: payload.firstName,
				lastName: payload.lastName,
				email: !isChildOrder ? payload.email : null,
				phone: !isChildOrder ? payload.phone : null,
				birthNumber: isEUInsurance ? '000000/0000' : payload.birthNumber,
				addressStreetAndStreetNumber: payload.address,
				addressCity: payload.city,
				addressZipCode: payload.zipCode,
				...(payload.countryId && { countryId: payload.countryId }),
				...(payload.euInsuranceNumber && { euInsuranceNumber: payload.euInsuranceNumber }),
				...(payload.contractualInsurance && {
					contractualInsurance: payload.contractualInsurance
				}),
				...(payload.insurance && { insuranceCompanyId: parseInt(payload.insurance, 10) })
			}

			// For annual-program-family, each cart product maps to one order product.
			const orderProducts =
				primaryProduct.type === CartProductType.ANNUAL_PROGRAM_FAMILY
					? cartProducts.map(p => ({
							productId: getProductId(p),
							modules: getProductModules(p)
						}))
					: [
							{
								productId: getProductId(primaryProduct),
								modules: getProductModules(primaryProduct)
							}
						]

			const body: OrderRequest = {
				type: getOrderApiType(primaryProduct),
				patient,
				...(isChildOrder && {
					customer: {
						firstName: payload.parent.firstName,
						lastName: payload.parent.lastName,
						email: payload.parent.email,
						phone: payload.parent.phone
					}
				}),
				orderProducts,
				agreedToTermsOfService: payload.terms,
				agreedToPersonalDataProtection: payload.privacyPolicy,
				agreedToPersonalDataProcessingForMarketingPurposes: payload.marketing,
				...(payload.message && { note: payload.message })
			}

			return createOrder(body)
		},

		onSuccess: (data, variables) => {
			toast.success('Objednávka bola úspešne odoslaná!', {
				position: 'top-right',
				className: '!bg-medante-pink-secondary',
				autoClose: 5000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true
			})

			if (data.data.order?.ulid) {
				saveOrderUlid(data.data.order.ulid)
				if (isAllAnnualProgramFamily(variables.cartProducts)) {
					commitGa4FamilyPurchaseSnapshot(data.data.order.ulid)
				} else {
					clearGa4PendingFamilyPurchase()
				}
			}

			if (data.data.payFormHtml) {
				savePayFormHtml(data.data.payFormHtml)
				router.push('/order/payment')
			} else if (data.data.order?.ulid) {
				router.push({
					pathname: '/thank-you/[orderId]',
					params: { orderId: data.data.order.ulid }
				})
			} else {
				router.push('/order')
			}
		},

		onError: error => {
			if (
				error instanceof AxiosError &&
				error.response?.data?.errorTranslatable === true &&
				error.response?.data?.errorTranslation
			) {
				toast.error(error.response.data.errorTranslation, CREATE_ORDER_ERROR_TOAST_OPTIONS)
				return
			}

			if (error instanceof AxiosError) {
				const mapped = getMappedCreateOrderErrorMessage(error.response?.data)
				if (mapped) {
					toast.error(mapped, CREATE_ORDER_ERROR_TOAST_OPTIONS)
					return
				}
			}

			toast.error(
				'Nastala chyba pri odosielaní objednávky. Skúste to prosím znovu.',
				CREATE_ORDER_ERROR_TOAST_OPTIONS
			)
		}
	})
}
